// External Libraries
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { Subscription, combineLatest, map, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

// Frontegg Services
import { FronteggAppService, FronteggAuthService } from '@frontegg/angular';

// Project Services
import { AuthService } from '../../services/auth/auth.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { SignOutService } from '../../services/sign-out-state/sign-out-state.service';
import { UserPrivilegesService } from '../../services/auth-privileges/auth-privileges.service';
import { WhatFixService } from '../../services/whatFix/whatfix.service';
import { TenantApiService } from '../../services/tenant-api/tenant-api.service';
import { User } from '@frontegg/redux-store';
import { BannerComponent } from '../../components/banner/banner.component';
import { UrlVerificationComponent } from '@ai-document-processor/shared-libraries';

/** Frontegg user including the custom claims added by Etrieve. */
type TranscriptsUser = User & { customClaims?: { principal_id?: string; display_name?: string } };

/**
 * Page shell for Intelligent Transcript Processing (`/transcripts`).
 * Handles tenant/URL verification, ITP access checks, authentication and the banner,
 * and hosts the transcript pages in its router outlet.
 */
@Component({
  selector: 'app-transcript-processing',
  templateUrl: './transcript-processing.component.html',
  styleUrls: ['./transcript-processing.component.scss'],
  imports: [BannerComponent, RouterOutlet, TranslatePipe, UrlVerificationComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TranscriptProcessingComponent implements OnInit, OnDestroy {
  private fronteggAuthService = inject(FronteggAuthService);
  private fronteggAppService = inject(FronteggAppService);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private router = inject(Router);
  private signOutService = inject(SignOutService);
  private userPrivilegesService = inject(UserPrivilegesService);
  private whatFix = inject(WhatFixService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private destroyRef = inject(DestroyRef);
  tenantService = inject(TenantApiService);

  private subscription = new Subscription();

  isAccessEnabled = signal<boolean | null>(null);
  isAccessFlagLoading = signal(true);
  isUrlVerified = signal(false);
  isLoading = signal(true);
  featureFlagErrorText = signal('');
  user = signal<TranscriptsUser | null>(null);
  isAccessITPAdmin = signal(false);
  transcriptProcessingFlag = signal(false);
  hasWebCaptureAccess = signal(true);
  webCaptureFeatureFlag = signal(true);

  private tenantId = '';
  private vanityName = '';
  private userPrincipalId = '';

  constructor() {
    this.fronteggAppService.isLoading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isLoading => this.isLoading.set(isLoading));
  }

  /**
   * Sets up query param handling (tenant identification), feature flag checks and sign-out logic.
   */
  ngOnInit(): void {
    this.handleCachedParams();

    this.subscription.add(
      this.route.queryParams.subscribe(params => this.handleQueryParams(params))
    );
  }

  /**
   * Angular lifecycle hook that cleans up subscriptions.
   */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Handles cached query parameters from window object.
   */
  private handleCachedParams(): void {
    const cachedParams = (window as any)._queryParamsCache;
    if (cachedParams && (cachedParams.tenantId || cachedParams.vanityName)) {
      this.tenantId = cachedParams.tenantId;
      this.vanityName = cachedParams.vanityName;

      if (this.tenantId) {
        localStorage.setItem('tenantId', this.tenantId);
      }
      if (this.vanityName) {
        localStorage.setItem('vanityName', this.vanityName);
      }
    }
  }

  /**
   * Handles query parameters for tenant identification.
   * @param params - The query parameters from the URL
   */
  private handleQueryParams(params: Params): void {
    this.tenantId = this.getQueryParamCaseInsensitive(params, 'tenantId') || '';
    this.vanityName = this.getQueryParamCaseInsensitive(params, 'vanityName') || '';
    if (this.tenantId) {
      localStorage.setItem('tenantId', this.tenantId);
    }
    if (this.vanityName) {
      localStorage.setItem('vanityName', this.vanityName);
    }

    // Clear query parameters from the URL but keep the route path
    if (this.tenantId || this.vanityName) {
      const urlWithoutParams = this.location.path().split('?')[0];
      this.location.replaceState(urlWithoutParams);
    }

    if ((this.tenantId && !this.vanityName) || (!this.tenantId && this.vanityName)) {
      console.error('Both tenantId and vanityName must be provided together.');
      this.clearTenantInfo();
    }

    if (this.tenantId && this.vanityName) {
      this.validateTenantInfo();
    } else {
      this.checkFeatureFlag();
    }

    this.subscription.add(
      this.signOutService.signOut$.subscribe(signOut => {
        if (signOut && !this.signOutService.isFilingContent) {
          this.signOutService.signOut();
        }
      })
    );
  }

  /**
   * Validates tenant information by cross-referencing tenantId and vanityName.
   */
  private validateTenantInfo(): void {
    this.subscription.add(
      this.tenantService.getTenantIdFromVanity(this.vanityName).subscribe({
        next: response => {
          if (response.tenantID === this.tenantId) {
            this.subscription.add(
              this.tenantService.getTenantUrls(this.tenantId).subscribe({
                next: tenantUrls => {
                  localStorage.setItem('contentURL', tenantUrls.contentUrl);
                  this.checkFeatureFlag();
                },
                error: error => {
                  console.error('Error fetching tenant URLs:', error);
                  this.checkFeatureFlag();
                }
              })
            );
          } else {
            console.error('Invalid tenant: vanity name mismatch');
            this.clearTenantInfo();
          }
        },
        error: error => {
          console.error('Invalid tenant information', error);
          this.clearTenantInfo();
        }
      })
    );
  }

  /**
   * Check the feature flag based on tenantId from localStorage.
   */
  private checkFeatureFlag(): void {
    const tenantId = localStorage.getItem('tenantId');
    this.subscription.add(
      this.fronteggAuthService.authState$.subscribe(authState => {
        if (authState.user) {
          const user = authState.user as TranscriptsUser;
          this.user.set(user);
          this.userPrincipalId = user.customClaims?.principal_id ?? '';
        }
      })
    );

    if (tenantId) {
      this.runFeatureFlagCheck(tenantId, this.userPrincipalId);
    } else {
      this.isAccessFlagLoading.set(false);
      this.isUrlVerified.set(false);
      this.isAccessEnabled.set(null);
    }
  }

  /**
   * Runs the feature flag checks for the provided tenant.
   * Access to /transcripts requires the `license-itp` flag; Virtual Printer only tenants are sent to
   * Unauthorized. `web-capture-access` only controls whether the app switcher offers Web Capture.
   * @param tenantId - the provided tenantId
   * @param userId - principal ID
   */
  private runFeatureFlagCheck(tenantId: string, userId: string): void {
    this.isAccessFlagLoading.set(true);
    this.featureFlagService
      .initialize({ userId, tenantId })
      .then(() => {
        this.subscription.add(
          this.featureFlagService
            .getFlag('license-itp', false)
            .pipe(
              switchMap(itpFlagValue => {
                this.transcriptProcessingFlag.set(itpFlagValue);
                return combineLatest([
                  this.featureFlagService.getFlag('web-capture-access', false),
                  this.featureFlagService.getFlag('virtual-printer-access', false)
                ]).pipe(
                  map(([webCaptureFlag, virtualPrinterFlag]) => ({
                    itpFlagValue,
                    webCaptureFlag,
                    virtualPrinterOnly: !!virtualPrinterFlag && !webCaptureFlag
                  }))
                );
              })
            )
            .subscribe(({ itpFlagValue, webCaptureFlag, virtualPrinterOnly }) => {
              if (virtualPrinterOnly) {
                this.router.navigate(['unauthorized'], {
                  state: { displayName: this.user()?.customClaims?.display_name }
                });
                return;
              }

              this.isAccessFlagLoading.set(false);

              if (itpFlagValue) {
                this.isAccessEnabled.set(true);
                this.startFronteggAuth();
                this.whatFix.initializeWithContext(this.tenantId, '');
                this.webCaptureFeatureFlag.set(webCaptureFlag);
              } else {
                this.isUrlVerified.set(false);
                this.isAccessEnabled.set(false);
                this.featureFlagErrorText.set('webCaptureFeatureFlagError');
              }
            })
        );
      })
      .catch(() => {
        this.isAccessFlagLoading.set(false);
        this.isUrlVerified.set(false);
        this.isAccessEnabled.set(false);
      });
  }

  /**
   * Re-verifies the tenant URL and triggers feature flag check based on the tenant ID
   */
  onSuccessfulVerification(): void {
    const tenantId = localStorage.getItem('tenantId');

    if (tenantId) {
      this.isAccessFlagLoading.set(true);
      this.runFeatureFlagCheck(tenantId, this.userPrincipalId);
    }
  }

  /**
   * Starts Frontegg Authentication flow
   */
  private startFronteggAuth(): void {
    this.isUrlVerified.set(true);
    const vanityName = localStorage.getItem('vanityName');
    this.tenantId = localStorage.getItem('tenantId') || '';

    if (!this.tenantId || !vanityName) {
      this.isUrlVerified.set(false);
      return;
    }

    this.subscription.add(
      this.fronteggAuthService.authState$.subscribe(authState => {
        if (!authState.isAuthenticated && !this.isLoading()) {
          this.loginWithRedirect(this.tenantId, vanityName);
          return;
        }

        if (!authState.user?.accessToken) {
          return;
        }

        if (authState.user.tenantId !== this.tenantId) {
          this.signOut();
          return;
        }

        this.user.set(authState.user);
        this.authService.setAuthToken(authState.user.accessToken);

        // Fetch privileges immediately so child components don't wait on feature flags
        this.userPrivilegesService
          .getUserPrivileges(authState.user.accessToken)
          .then(privileges => {
            const redirectRoute = this.consumeRedirectAfterLogin();

            if (!this.hasItpAccess()) {
              this.router.navigate(['unauthorized'], {
                state: { displayName: this.user()?.customClaims?.display_name }
              });
              return;
            }

            this.hasWebCaptureAccess.set(
              privileges.applicationPrivileges.some(p => p.code === 'WEB_CAPTURE_ACCESS')
            );
            this.initializeAdminFlag();

            if (redirectRoute && redirectRoute !== this.router.url.split('?')[0]) {
              this.router.navigateByUrl(redirectRoute);
            }
          });
      })
    );
  }

  /**
   * Reads and clears the route the user originally requested before the login redirect.
   * Cleared up front so a failed access check can't leave a stale redirect behind.
   */
  private consumeRedirectAfterLogin(): string | null {
    const redirectRoute = localStorage.getItem('redirectAfterLogin');
    localStorage.removeItem('redirectAfterLogin');
    return redirectRoute;
  }

  /**
   * Sets `isAccessITPAdmin` from the user's roles and the `license-itp` flag.
   */
  private initializeAdminFlag(): void {
    this.featureFlagService
      .getFlag('license-itp', false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(flagValue => {
        this.isAccessITPAdmin.set(this.hasItpAdminRole() && flagValue);
      });
  }

  /**
   * Redirects the user to the login page with the specified tenant id and vanityName
   * @param tenantId - the id of the tenant to log in to
   * @param vanityName - the vanityName name associated with the tenant
   */
  private loginWithRedirect(tenantId: string, vanityName: string): void {
    const urlWithoutParams = this.location.path().split('?')[0];

    localStorage.setItem('redirectAfterLogin', urlWithoutParams);

    this.fronteggAuthService.loginWithRedirect({
      tenantId: tenantId,
      organization: vanityName
    });
  }

  /**
   * Checks if user has any ITP Processor roles
   */
  hasItpAccess(): boolean {
    return this.userPrivilegesService.hasItpProcessorRole(this.user()?.roles);
  }

  /**
   * Checks if user has ITP Admin role
   */
  private hasItpAdminRole(): boolean {
    return this.userPrivilegesService.hasItpAdminRole(this.fronteggAuthService.user?.roles);
  }

  /**
   * Signs out the user of the application and redirects them back to the log in page
   */
  signOut(): void {
    this.userPrivilegesService.clearCache();
    this.signOutService.setSignOutState(true);
  }

  /**
   * Clears tenant information from localStorage and resets access flags.
   */
  private clearTenantInfo(): void {
    localStorage.removeItem('tenantId');
    localStorage.removeItem('vanityName');
    localStorage.removeItem('contentURL');
    this.tenantId = '';
    this.vanityName = '';
    this.isAccessEnabled.set(false);
    this.isAccessFlagLoading.set(false);
  }

  /**
   * Reads a query parameter regardless of key casing.
   * @param params - URL query parameters
   * @param paramName - Name of the parameter to retrieve
   */
  private getQueryParamCaseInsensitive(params: Params, paramName: string): string | null {
    const key = Object.keys(params).find(k => k.toLowerCase() === paramName.toLowerCase());

    return key ? params[key] : null;
  }
}
